import { cn, InfoPopover, Stack } from 'component-library';
import { ReactNode, SetStateAction, useMemo, useState } from 'react';

import { GraphTileType } from '@/api/rest/resources/types/widget';
import { useScreenSize } from '@/hooks/useScreenSize';
import { isReactNode } from '@/utils/isReactNode';

import { GRAPHS_WITH_CUSTOM_BENCHMARKS } from '../constants';
import { ChartEditDropdown } from './ChartEditDropdown';
import { ChartZoomStateHook } from './ChartZoom';
import { CustomBenchmarkDialog } from './CustomBenchmarkDialog';

export type ChartHeadingProps = {
  heading: ReactNode;
  popover?:
    | ReactNode
    | {
        title: ReactNode;
        body: ReactNode;
      };
  className?: string;
  indicator?: GraphTileType;
  selectedBenchmark?: string | null;
  setSelectedBenchmark?: (id: string | null) => void;
  showCustomBenchmark?: boolean;
} & Partial<ChartZoomStateHook>;

export const ChartHeading = ({
  popover,
  className,
  heading,
  zoom,
  setZoom,
  indicator,
  selectedBenchmark,
  setSelectedBenchmark,
  showCustomBenchmark,
}: ChartHeadingProps) => {
  const isSmallScreen = useScreenSize() === 'small';

  const [isBenchmarkDialogOpen, setIsBenchmarkDialogOpen] = useState(false);

  const popoverComponent = useMemo(() => {
    if (!popover) return null;

    if (isReactNode(popover)) return popover;

    return <InfoPopover {...popover} />;
  }, [popover]);

  const handleSetBenchmark = (value: SetStateAction<string | null>) => {
    if (!setSelectedBenchmark) return;

    if (typeof value === 'function') {
      setSelectedBenchmark(value(selectedBenchmark ?? null));
    } else {
      setSelectedBenchmark(value);
    }
  };

  const showZoom = zoom !== undefined && setZoom !== undefined;
  const hasCustomBenchmark = (indicator && GRAPHS_WITH_CUSTOM_BENCHMARKS.has(indicator)) || !!showCustomBenchmark;
  const showChartEditDropdown = showZoom || hasCustomBenchmark;

  return (
    <>
      <Stack
        direction='row'
        spacing={2}
        className={cn('self-start', className, { 'justify-between': showChartEditDropdown === true })}
        centerMain
      >
        <span className='typography-body1'>
          {/* TODO: MVP-3284 Update copy for historicalGraphHeadline */}
          {heading}
          {!isSmallScreen && (
            <div className='relative inline-block'>
              <div className='absolute -top-[13px] left-2'>{popoverComponent}</div>
            </div>
          )}
        </span>
        {showChartEditDropdown && (
          <ChartEditDropdown
            zoom={zoom}
            setZoom={setZoom}
            indicator={indicator}
            selectedBenchmark={selectedBenchmark}
            setSelectedBenchmark={setSelectedBenchmark ? handleSetBenchmark : undefined}
            onOpenBenchmarkDialog={() => setIsBenchmarkDialogOpen(true)}
          />
        )}
      </Stack>
      {indicator && (
        <CustomBenchmarkDialog
          isOpen={isBenchmarkDialogOpen}
          onClose={() => setIsBenchmarkDialogOpen(false)}
          indicatorType={indicator}
        />
      )}
    </>
  );
};
